import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip component that renders at document body level using portals
 * This ensures tooltips appear above all elements regardless of stacking contexts
 */
function AbilityTooltip({ children, tooltipText }) {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const elementRef = useRef(null);
  const tooltipRef = useRef(null);
  
  // Debug hover state
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && tooltipText) {
      console.log(`AbilityTooltip: isHovered=${isHovered}, tooltipText="${tooltipText.substring(0, 30)}..."`);
    }
  }, [isHovered, tooltipText]);

  useEffect(() => {
    if (!isHovered || !elementRef.current) return;

    const updatePosition = () => {
      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const tooltipWidth = 250; // Tooltip width
      const tooltipHeight = tooltipRef.current?.offsetHeight || 100;
      const spacing = 8;
      const viewportPadding = 10; // Padding from viewport edges

      // Calculate center position of element
      const elementCenterX = rect.left + (rect.width / 2);
      const elementTop = rect.top;
      const elementBottom = rect.bottom;

      // Try positioning above first
      let top = elementTop - tooltipHeight - spacing;
      let left = elementCenterX;
      let positionAbove = true;

      // Check if tooltip would go off top of screen
      if (top < viewportPadding) {
        // Try positioning below instead
        top = elementBottom + spacing;
        positionAbove = false;
        
        // If it would go off bottom, position at top of viewport
        if (top + tooltipHeight > window.innerHeight - viewportPadding) {
          top = viewportPadding;
        }
      }

      // Check if tooltip would go off bottom of screen
      if (top + tooltipHeight > window.innerHeight - viewportPadding) {
        // Position above if we're not already
        if (!positionAbove) {
          top = elementTop - tooltipHeight - spacing;
          // If still off screen, position at bottom of viewport
          if (top < viewportPadding) {
            top = window.innerHeight - tooltipHeight - viewportPadding;
          }
        } else {
          // Already above, position at top of viewport
          top = viewportPadding;
        }
      }

      // Check horizontal positioning - ensure tooltip doesn't go off left or right
      const tooltipHalfWidth = tooltipWidth / 2;
      if (left - tooltipHalfWidth < viewportPadding) {
        // Too far left, adjust to stay within viewport
        left = tooltipHalfWidth + viewportPadding;
      } else if (left + tooltipHalfWidth > window.innerWidth - viewportPadding) {
        // Too far right, adjust to stay within viewport
        left = window.innerWidth - tooltipHalfWidth - viewportPadding;
      }

      setPosition({ top, left });
    };

    // Initial position - use requestAnimationFrame to ensure tooltip is rendered and measured
    let rafId = null;
    const scheduleUpdate = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Use double RAF to ensure tooltip is in DOM and measured
        requestAnimationFrame(() => {
          updatePosition();
        });
      });
    };
    
    scheduleUpdate();
    
    // Update position on scroll or resize
    const handleScroll = () => scheduleUpdate();
    const handleResize = () => scheduleUpdate();
    
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isHovered]);

  return (
    <>
      <span
        ref={elementRef}
        onMouseEnter={() => {
          console.log('✅ AbilityTooltip: onMouseEnter fired!');
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          console.log('❌ AbilityTooltip: onMouseLeave fired!');
          setIsHovered(false);
        }}
        style={{ display: 'inline', pointerEvents: 'auto' }}
      >
        {children}
      </span>
      {tooltipText && createPortal(
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translateX(-35%)',
            padding: '8px 12px',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
            border: '1px solid #00bcd4',
            borderRadius: '4px',
            color: '#e0e0e0',
            fontSize: '12px',
            fontWeight: '500',
            whiteSpace: 'normal',
            width: '250px',
            maxWidth: 'calc(100vw - 20px)',
            minWidth: '200px',
            maxHeight: 'calc(100vh - 20px)',
            overflowY: 'auto',
            boxSizing: 'border-box',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 188, 212, 0.3)',
            fontFamily: "'Exo 2', sans-serif",
            letterSpacing: '0.5px',
            lineHeight: '1.4',
            textAlign: 'left',
            zIndex: 1000000, // Very high z-index to appear above expanded order cards (99999) and all other elements
            pointerEvents: 'none',
            opacity: isHovered ? 1 : 0,
            visibility: isHovered ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease',
          }}
        >
          {tooltipText}
        </div>,
        document.body
      )}
    </>
  );
}

export default AbilityTooltip;

